import { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  Calendar,
  MessageSquare,
  CheckCircle2,
  ShoppingCart,
  FileText,
  Clock,
  AlertCircle,
  User,
  Target,
  Receipt,
  Send,
} from "lucide-react";
import api from "../lib/api";

const ACTIVITY_ICONS = {
  call: Phone,
  email: Mail,
  meeting: Calendar,
  note: MessageSquare,
  task: CheckCircle2,
  order: ShoppingCart,
  quote: FileText,
  system: Target,
  statement_request: Receipt,
  email_sent: Send,
  visit: Calendar,
};

const ACTIVITY_COLORS = {
  call: "text-blue-600",
  email: "text-purple-600",
  meeting: "text-green-600",
  note: "text-gray-600",
  task: "text-orange-600",
  order: "text-emerald-600",
  quote: "text-yellow-600",
  system: "text-slate-600",
  statement_request: "text-indigo-600",
  email_sent: "text-teal-600",
  visit: "text-orange-600",
};

const emptyActivity = {
  activity_type: "note",
  title: "",
  description: "",
  due_date: "",
  duration_minutes: 0,
  outcome: "",
  completed: false,
};

export default function ClientTimeline({ clientId }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");

  useEffect(() => {
    fetchActivities();
  }, [clientId, filterType]);

  const fetchActivities = async () => {
    try {
      const params = new URLSearchParams({ client_id: clientId });
      if (filterType) params.append("activity_type", filterType);

      const response = await api.get(`/activities?${params}`);
      setActivities(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento attività");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (activityId) => {
    try {
      const activity = activities.find((a) => a.id === activityId);
      await api.put(`/activities/${activityId}`, {
        ...activity,
        completed: true,
      });
      toast.success("Attività completata");
      fetchActivities();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Oggi ${date.toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (diffDays === 1) {
      return "Ieri";
    } else if (diffDays < 7) {
      return `${diffDays} giorni fa`;
    } else {
      return date.toLocaleDateString("it-IT", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-20 bg-muted rounded" />
        <div className="h-20 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Select value={filterType || "all"} onValueChange={(value) => setFilterType(value === "all" ? "" : value)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Tutte le attività" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="call">Chiamate</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="meeting">Incontri</SelectItem>
              <SelectItem value="visit">Visite</SelectItem>
              <SelectItem value="note">Note</SelectItem>
              <SelectItem value="task">Task</SelectItem>
              <SelectItem value="order">Ordini</SelectItem>
              <SelectItem value="quote">Preventivi</SelectItem>
              <SelectItem value="statement_request">Richieste Estratto Conto</SelectItem>
              <SelectItem value="email_sent">Email Inviate</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {activities.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna attività registrata per questo cliente</p>
              <p className="text-sm mt-2">
                Inizia a tracciare le tue interazioni con il cliente
              </p>
            </CardContent>
          </Card>
        ) : (
          activities.map((activity) => {
            const Icon = ACTIVITY_ICONS[activity.activity_type] || MessageSquare;
            const colorClass =
              ACTIVITY_COLORS[activity.activity_type] || "text-gray-600";

            return (
              <Card
                key={activity.id}
                className={`relative ${
                  !activity.completed && activity.activity_type === "task"
                    ? "border-l-4 border-l-orange-500"
                    : ""
                }`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div
                      className={`p-2 rounded-full bg-muted ${colorClass} flex-shrink-0`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-base">
                            {activity.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(activity.created_at)}
                          </p>
                        </div>

                        {!activity.completed && activity.activity_type === "task" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleComplete(activity.id)}
                            className="flex-shrink-0"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Completa
                          </Button>
                        )}
                      </div>

                      {activity.description && (
                        <p className="text-sm mt-2 text-muted-foreground whitespace-pre-wrap">
                          {activity.description}
                        </p>
                      )}

                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        {activity.duration_minutes > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {activity.duration_minutes} min
                          </span>
                        )}
                        {activity.outcome && (
                          <span
                            className={`flex items-center gap-1 ${
                              activity.outcome === "positive"
                                ? "text-green-600"
                                : activity.outcome === "negative"
                                ? "text-red-600"
                                : ""
                            }`}
                          >
                            {activity.outcome === "positive" && "✓ Positivo"}
                            {activity.outcome === "neutral" && "○ Neutro"}
                            {activity.outcome === "negative" && "✗ Negativo"}
                          </span>
                        )}
                        {activity.due_date && !activity.completed && (
                          <span
                            className={`flex items-center gap-1 ${
                              new Date(activity.due_date) < new Date()
                                ? "text-red-600 font-medium"
                                : ""
                            }`}
                          >
                            <AlertCircle className="w-3 h-3" />
                            Scadenza: {new Date(activity.due_date).toLocaleDateString("it-IT")}
                          </span>
                        )}
                        {activity.completed && activity.activity_type === "task" && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-3 h-3" />
                            Completato
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
